// This module wraps backend calls for Special Events. It receives an injected
// `callApi` function (from useAuthenticatedApi) so that authentication headers
// are correctly applied.

// import { apiClient } from "./client"; // Unused import removed

export type CallApiFn = (config: {
  method: "GET" | "POST" | "PUT" | "DELETE";
  url: string;
  data?: unknown;
  params?: Record<string, unknown>;
}) => Promise<any>;

interface PaginatedResponse<T> {
  data: T[];
  meta: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
  };
}

// interface ApiResponse<T> { // Unused interface removed
//   data: T;
//   message?: string;
//   status: number;
// }

// API response structure from backend
interface ApiSpecialEventResponse {
  eventid: string;
  hotelid: string;
  eventname: string;
  imagefile: string;
  location: string;
  description: string;
  link: string;
  frequency: string;
  createdat?: string;
  updatedat?: string;
  _media?: {
    imagefile?: {
      key: string;
      originalValue: string;
      error?: string;
    };
  };
}

// Define the type here instead
export interface SpecialEventItem {
  eventId: string;
  hotelId: string;
  name: string;
  description: string;
  location: string;
  imageFile: string;
  link: string;
  frequency: string;
  createdAt: string;
  updatedAt: string;
}

// Raw API object for a special event from the backend
export interface ApiSpecialEvent {
  eventid: string;
  hotelid: string;
  eventname: string;
  description: string;
  location: string;
  imagefile: string;
  link: string;
  frequency: string;
  createdat: string;
  updatedat: string;
}

// Sample data for fallback
const specialEventsItems: SpecialEventItem[] = [
  {
    eventId: "sample-1",
    hotelId: "85895225-aa94-4da3-96de-a33f14bc4dee",
    name: "Sample Event",
    description: "A sample event for testing",
    location: "Main Hall",
    imageFile: "",
    link: "",
    frequency: "",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

// Helper function to transform API response to match our expected structure
const transformEvent = (event: ApiSpecialEventResponse): SpecialEventItem => {
  // Handle S3 URL from _media field if available, otherwise fall back to imagefile
  let imageFile: string = "";

  if (event._media?.imagefile?.key && !event._media.imagefile?.error) {
    // Use the signed URL from _media field (backend provides 'signedUrl' even though it's not in the type)
    const mediaImage = event._media.imagefile as any;
    imageFile = mediaImage.signedUrl || mediaImage.url || mediaImage.key;
  } else if (event.imagefile) {
    // Fall back to the direct imagefile field
    imageFile = event.imagefile;
  }

  return {
    eventId: event.eventid,
    hotelId: event.hotelid,
    name: event.eventname,
    description: event.description,
    location: event.location,
    imageFile,
    link: event.link,
    frequency: event.frequency,
    createdAt: event.createdat || new Date().toISOString(),
    updatedAt: event.updatedat || new Date().toISOString(),
  };
};

export const getSpecialEvents = async (
  callApi: CallApiFn,
  page: number = 1,
  limit: number = 20,
  search: string = ""
): Promise<PaginatedResponse<SpecialEventItem>> => {
  try {
    const params: Record<string, unknown> = { page, limit };
    if (search.trim()) {
      params.search = search.trim();
    }

    if (import.meta.env.DEV)
      console.log("getSpecialEvents: calling with params", params);
    const response = await callApi({
      method: "GET",
      url: `/management/hotel/events`,
      params,
    });
    if (import.meta.env.DEV) console.log("getSpecialEvents: response received");

    // Response may be { data, meta } or raw array
    const respData = response.data;

    const events: ApiSpecialEventResponse[] = Array.isArray(respData.data)
      ? respData.data
      : respData.data?.data || [];

    const meta = respData.meta ||
      respData.data?.meta || {
        totalCount: events.length,
        page,
        limit,
        totalPages: Math.ceil(events.length / limit),
      };

    return {
      data: events.map(transformEvent),
      meta: {
        page: meta.page || page,
        limit: meta.limit || limit,
        totalCount: meta.totalCount || events.length,
        totalPages: meta.totalPages || Math.ceil(events.length / limit),
      },
    };
  } catch (error) {
    console.error("getSpecialEvents error:", error);

    // Check if it's an authentication error
    if (
      error instanceof Error &&
      error.message.includes("Authentication required")
    ) {
      console.error("Authentication error in getSpecialEvents:", error);
      throw error; // Re-throw auth errors so they can be handled by the UI
    }

    // For other errors, return empty data
    console.error("getSpecialEvents fallback", error);
    return { data: [], meta: { page, limit, totalCount: 0, totalPages: 0 } };
  }
};

export const getSpecialEvent = async (
  callApi: CallApiFn,
  eventId: string
): Promise<SpecialEventItem> => {
  try {
    const response = await callApi({
      method: "GET",
      url: `/management/hotel/events/${eventId}`,
    });

    return transformEvent(response.data.data);
  } catch (error) {
    console.warn("API call failed, using fallback data:", error);
    const event = specialEventsItems.find((item) => item.eventId === eventId);
    if (!event) {
      throw new Error("Event not found");
    }
    return event;
  }
};

export const createSpecialEvent = async (
  callApi: CallApiFn,
  event: Omit<SpecialEventItem, "eventId" | "createdAt" | "updatedAt">
): Promise<SpecialEventItem> => {
  const payload = {
    name: event.name,
    description: event.description,
    location: event.location,
    imagefile: event.imageFile,
    link: event.link,
    frequency: event.frequency,
  };

  const resp = await callApi({
    method: "POST",
    url: `/management/hotel/events`,
    data: payload,
  });

  return transformEvent(resp.data.data as ApiSpecialEventResponse);
};

export const updateSpecialEvent = async (
  callApi: CallApiFn,
  eventId: string,
  updates: Partial<SpecialEventItem>
): Promise<SpecialEventItem> => {
  const payload: Record<string, unknown> = {};
  if (updates.name) payload.name = updates.name;
  if (updates.description) payload.description = updates.description;
  if (updates.location) payload.location = updates.location;
  if (Object.prototype.hasOwnProperty.call(updates, "imageFile")) {
    payload.imagefile = updates.imageFile ?? "";
  }
  if (updates.link) payload.link = updates.link;
  if (updates.frequency) payload.frequency = updates.frequency;

  const resp = await callApi({
    method: "PUT",
    url: `/management/hotel/events/${eventId}`,
    data: payload,
  });

  return transformEvent(resp.data.data as ApiSpecialEventResponse);
};

export const deleteSpecialEvent = async (
  callApi: CallApiFn,
  eventId: string
): Promise<void> => {
  await callApi({
    method: "DELETE",
    url: `/management/hotel/events/${eventId}`,
  });
};
